<template>
    <Teleport to="body">
        <!-- Explicit duration: only the overlay and the panel transition, never the root
             element, so Vue cannot sniff when the leave ends. -->
        <Transition name="sheet" :duration="250">
            <!-- Deliberately not a dialog component: reka's dismiss layer and focus trap both
                 fight the size dropdowns, whose panels teleport to <body> and so count as
                 "outside". A sheet this simple only needs an overlay and a panel. -->
            <div v-if="modelValue" class="fixed inset-0 z-[9998]" role="dialog" aria-modal="true" :aria-label="title">
                <div class="sheet-overlay absolute inset-0 bg-black/50" @click="modelValue = false" />

                <div class="sheet-panel absolute inset-x-0 bottom-0 z-10 rounded-t-lg bg-white shadow-xl">
                    <div class="flex w-full items-center justify-between border-b border-gray-200 p-3 pt-5">
                        <h3 class="text-2xl font-semibold" v-text="title" />

                        <button
                            type="button"
                            class="cursor-pointer opacity-50 transition outline-none hover:opacity-100 focus:outline-none"
                            @click.prevent="modelValue = false"
                        >
                            <XIcon />
                        </button>
                    </div>

                    <div class="w-full p-3">
                        <slot />
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { XIcon } from '@lucide/vue';
import { onBeforeUnmount, watch } from 'vue';

/**
 * A bottom sheet: full width, anchored to the bottom, as tall as its content. The shared
 * Modal offers only fullscreen-on-mobile or a centered panel, neither of which is this.
 * It opens on tap only, so it never renders during SSR and needs no teleport guard.
 */
defineProps<{
    title: string;
}>();

const modelValue = defineModel<boolean>({ required: true });

function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
        modelValue.value = false;
    }
}

// Prevent body scroll, same mechanism as BaseModal: overflow alone is not
// enough, iOS Safari keeps touch-scrolling the page unless the body is fixed.
let scrollY = 0;
let isBodyLocked = false;

function lockBodyScroll(): void {
    if (isBodyLocked) {
        return;
    }

    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    // A fixed body sits at scroll 0, so a small downward swipe would read as pull-to-refresh.
    document.documentElement.style.overscrollBehaviorY = 'none';
    isBodyLocked = true;
}

function unlockBodyScroll(): void {
    if (!isBodyLocked) {
        return;
    }

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.overscrollBehaviorY = '';
    window.scrollTo(0, scrollY);
    isBodyLocked = false;
}

watch(modelValue, (open: boolean) => {
    if (open) {
        lockBodyScroll();
        window.addEventListener('keydown', onKeydown);

        return;
    }

    unlockBodyScroll();
    window.removeEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
    unlockBodyScroll();
    window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
/* The overlay fades while the panel slides, both directions. */
.sheet-enter-active .sheet-overlay,
.sheet-leave-active .sheet-overlay {
    transition: opacity 0.25s;
}

.sheet-enter-from .sheet-overlay,
.sheet-leave-to .sheet-overlay {
    opacity: 0;
}

.sheet-enter-active .sheet-panel,
.sheet-leave-active .sheet-panel {
    transition: transform 0.25s ease-out;
}

.sheet-enter-from .sheet-panel,
.sheet-leave-to .sheet-panel {
    transform: translateY(100%);
}
</style>
